
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { PaymentType } from '@/types/payment';
import { mockPayments } from './mockData';
import { usePaymentFilters } from './usePaymentFilters';
import { usePagination } from './usePagination';
import { useBiweeklyReport } from './useBiweeklyReport';
import { PaymentFilters } from './types';

export const useReferrerPayments = () => {
  const { toast } = useToast();
  const [payments, setPayments] = useState<PaymentType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Load initial data
  useEffect(() => {
    const fetchPayments = async () => {
      setIsLoading(true);
      try {
        // In a real app, this would be an API call
        await new Promise(resolve => setTimeout(resolve, 800));
        setPayments(mockPayments);
      } catch (error) {
        console.error('Error fetching referrer payments:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPayments();
  }, []);
  
  const { filteredPayments, filters, setFilters } = usePaymentFilters(payments);
  const { paginatedItems: paginatedPayments, pagination, setPage } = usePagination(filteredPayments);
  const { generateBiweeklyReport } = useBiweeklyReport(payments);
  
  // Payment actions
  const handleApprovePayment = async (id: string) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setPayments(prevPayments => 
        prevPayments.map(payment => 
          payment.id === id 
            ? { ...payment, status: 'completed' } 
            : payment
        )
      );
      
      toast({
        title: "Payment approved",
        description: "The payment has been marked as completed.",
      });
    } catch (error) {
      console.error('Error approving payment:', error);
      toast({
        title: "Error",
        description: "Failed to approve payment. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const handleRejectPayment = async (id: string) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setPayments(prevPayments => 
        prevPayments.map(payment => 
          payment.id === id 
            ? { ...payment, status: 'rejected', rejectionReason: 'Rejected by admin' } 
            : payment
        )
      );
      
      toast({
        title: "Payment rejected",
        description: "The payment has been rejected.",
      });
    } catch (error) {
      console.error('Error rejecting payment:', error);
      toast({
        title: "Error",
        description: "Failed to reject payment. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  return {
    payments,
    filteredPayments,
    paginatedPayments,
    isLoading,
    filters,
    pagination,
    setPage,
    setSearchQuery: (searchQuery: string) => setFilters(prev => ({ ...prev, searchQuery })),
    setPaymentMethod: (paymentMethod: string) => setFilters(prev => ({ ...prev, paymentMethod })),
    setDateRange: (dateRange: any) => setFilters(prev => ({ ...prev, dateRange })),
    setActiveTab: (activeTab: string) => setFilters(prev => ({ ...prev, activeTab })),
    handleApprovePayment,
    handleRejectPayment,
    generateBiweeklyPaymentReport: generateBiweeklyReport
  };
};
